import React, { useState, useEffect } from 'react';
import { Shield, Eye, EyeOff, RefreshCw } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import axios from 'axios';
import { API } from '../../lib/axiosClient';

export const TwoFactorStatus = ({ onDisabled }) => {
  const { t } = useLanguage();
  const [status, setStatus] = useState(null);
  const [showDisable, setShowDisable] = useState(false);
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [regenLoading, setRegenLoading] = useState(false);
  const [newBackupCodes, setNewBackupCodes] = useState(null);
  const [regenPw, setRegenPw] = useState('');
  const [showRegen, setShowRegen] = useState(false);

  useEffect(() => {
    fetchStatus();
  }, []);

  const fetchStatus = async () => {
    try {
      const res = await axios.get(`${API}/2fa/status`);
      setStatus(res.data);
    } catch (e) { /* ignore */ }
  };

  const disable2FA = async () => {
    if (!password) { setError('Passwort erforderlich'); return; }
    setLoading(true);
    setError('');
    try {
      const res = await axios.post(`${API}/2fa/disable`, { password });
      onDisabled?.();
      fetchStatus();
      setShowDisable(false);
    } catch (e) {
      setError(e.response?.data?.detail || e.message || t('twoFactor.deactivationFailed'));
    } finally {
      setLoading(false);
    }
  };

  const regenCodes = async () => {
    if (!regenPw) { setError(t('twoFactor.passwordRequired')); return; }
    setRegenLoading(true);
    setError('');
    try {
      const res = await axios.post(`${API}/2fa/regenerate-backup-codes`, { password: regenPw });
      setNewBackupCodes(res.data.backup_codes);
      fetchStatus();
    } catch (e) {
      setError(e.response?.data?.detail || e.message || t('twoFactor.regenError'));
    } finally {
      setRegenLoading(false);
    }
  };

  if (!status) return null;

  return (
    <div className="space-y-4" data-testid="2fa-status">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Shield className="w-5 h-5 text-emerald-500" />
          <span className="font-semibold text-sm">{t('twoFactor.title')}</span>
        </div>
        <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-400 font-medium">{t('catalog.active')}</span>
      </div>

      <div className="grid grid-cols-2 gap-3 text-xs">
        <div className="p-2 bg-slate-800 rounded-lg">
          <p className="text-slate-500">{t('twoFactor.backupCodes')}</p>
          <p className="font-semibold">{status.backup_codes_remaining} {t('twoFactor.remaining')}</p>
        </div>
        <div className="p-2 bg-slate-800 rounded-lg">
          <p className="text-slate-500">Policy</p>
          <p className="font-semibold capitalize">{status.policy === 'required' ? t('twoFactor.requiredPolicy') : status.policy === 'optional' ? t('twoFactor.optionalPolicy') : t('twoFactor.disabledPolicy')}</p>
        </div>
      </div>

      {/* Backup-Codes regenerieren */}
      {!showRegen ? (
        <button onClick={() => setShowRegen(true)} data-testid="2fa-regen-btn"
          className="w-full py-2 px-3 bg-slate-700 hover:bg-slate-600 text-xs text-white rounded-lg flex items-center justify-center gap-2 transition-colors">
          <RefreshCw className="w-3.5 h-3.5" />
          {t('twoFactor.generateNewCodes')}
        </button>
      ) : (
        <div className="space-y-2 p-3 bg-slate-800 rounded-lg">
          <input type="password" value={regenPw} onChange={e => setRegenPw(e.target.value)}
            placeholder={t("twoFactor.confirmPasswordPlaceholder")}
            className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded text-sm focus:border-emerald-500 focus:outline-none"
            data-testid="2fa-regen-password" />
          <button onClick={regenCodes} disabled={regenLoading} data-testid="2fa-regen-confirm-btn"
            className="w-full py-1.5 bg-amber-600 hover:bg-amber-700 text-white text-xs rounded transition-colors">
            {regenLoading ? t('twoFactor.generating') : t('twoFactor.generateNewCodes')}
          </button>
        </div>
      )}

      {newBackupCodes && (
        <div className="space-y-2">
          <p className="text-xs text-amber-400 font-semibold">{t('twoFactor.newBackupCodes')}</p>
          <div className="grid grid-cols-2 gap-1 font-mono text-xs">
            {newBackupCodes.map((c, i) => (
              <div key={`bc-${i}-${c}`} className="px-2 py-1 bg-slate-700 rounded text-center">{c}</div>
            ))}
          </div>
        </div>
      )}

      {/* Deaktivieren */}
      {status.policy !== 'required' && (
        !showDisable ? (
          <button onClick={() => setShowDisable(true)} data-testid="2fa-disable-btn"
            className="w-full py-2 px-3 bg-red-900/30 hover:bg-red-900/50 text-red-400 text-xs rounded-lg border border-red-800/30 transition-colors">
            {t('twoFactor.disable')}
          </button>
        ) : (
          <div className="space-y-2 p-3 bg-red-900/20 border border-red-800/30 rounded-lg">
            <p className="text-xs text-red-400">Passwort zur Bestätigung eingeben:</p>
            <div className="relative">
              <input type={showPw ? 'text' : 'password'} value={password}
                onChange={e => { setPassword(e.target.value); setError(''); }}
                placeholder={t('twoFactor.passwordPlaceholder')}
                className="w-full px-3 py-2 bg-slate-700 border border-red-800/50 rounded text-sm focus:border-red-500 focus:outline-none pr-10"
                data-testid="2fa-disable-password" />
              <button onClick={() => setShowPw(!showPw)} className="absolute right-2 top-2 text-slate-400">
                {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            <div className="flex gap-2">
              <button onClick={disable2FA} disabled={loading} data-testid="2fa-disable-confirm-btn"
                className="flex-1 py-1.5 bg-red-600 hover:bg-red-700 text-white text-xs rounded transition-colors">
                {loading ? t('twoFactor.deactivating') : t('twoFactor.confirm')}
              </button>
              <button onClick={() => { setShowDisable(false); setError(''); }}
                className="flex-1 py-1.5 bg-slate-700 hover:bg-slate-600 text-white text-xs rounded transition-colors">
                {t('common.cancel')}
              </button>
            </div>
          </div>
        )
      )}

      {error && <p className="text-xs text-red-400">{error}</p>}
    </div>
  );
};
